/* eslint-disable react/jsx-boolean-value */
import '@/theme/partials/_button.scss';

import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import {
  IonButton, IonButtons, IonCol, IonGrid, IonIcon, IonImg, IonInput, IonLabel, IonList, IonRow,
  IonSelect,
  IonSelectOption,
  isPlatform,
} from '@ionic/react';
import { DniReader } from '@pepper/capacitor-dni-reader';
import classNames from 'classnames';
import { idniecap } from 'idniecap';
import { chevronDownOutline } from 'ionicons/icons';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import CalculatorIcon from '@/assets/icons/calculator.svg';
import EditIcon from '@/assets/icons/managed/edit_managed.svg';
import IconEditManaged from '@/assets/icons/managed/edit_managed_selected.svg';
import InfoIcon from '@/assets/icons/managed/info_managed.svg';
import ENVIRONMENT from '@/environments/environment';
import useModal from '@/hooks/useModal';
import { ClientTaxonomyProps } from '@/interfaces/AnalyticsInterface/AnalyticsInterface';
import { ManagedModalRoutes } from '@/interfaces/ManagedModalInterface/ManagedModalInterface';
import { checkpreferredOfferAndNoPreferredOffer } from '@/redux/commercialOffers/commercialOffers';
import { saveVersionControl } from '@/redux/configOptions/config';
import { getSimulations } from '@/redux/eDniModal/eDniModalAPI';
import { CommercialOfferResult, CommercialOfferType } from '@/redux/eDniModal/eDniModalInterface';
import { saveInvoiceManagedData } from '@/redux/invoiceManaged/invoiceManaged';
import { InvoiceData } from '@/redux/invoiceManaged/invoiceManagedInterface';
import {
  clearBudgetSimulations,
  clearSimulationTerm, saveBudgetSimulations, saveIsPreferredOffer, saveOrderManagedData,
} from '@/redux/orderManaged/orderManaged';
import {
  DescriptionOrderLocalized, OrderDetailsResponseContent, Product,
  TitleOrderLocalized,
} from '@/redux/orderManaged/orderManagedInterface';
import { Analytics } from '@/services/analytics';
import { requestVersionControl } from '@/services/api/version';
import InsightService from '@/services/insights';
import { storageService } from '@/services/storage';
import { RootState } from '@/store';
import { checkCommercialOfferAvailabilityECO } from '@/utils/commercialOfferHelper';
import { formatPrice } from '@/utils/pricesHelper';
import { VALIDATOR_PATTERN } from '@/utils/validators';

import styles from './LocalizedBudget.module.scss';
import ModalOrderDetail from './ModalOrderDetail/ModalOrderDetail';
import ModalSimulation from './ModalSimulation/ModalSimulation';
import { VersionControl } from '@/interfaces/VersionControlInterface/VersionControlInterface';

interface LocalizedBugetOrderProps {
  nextStep: (arg0: ManagedModalRoutes, isDocument?: boolean, isPreferredSelected?: boolean) => void;
  handleLoading: (arg0: boolean) => void;
  isDocument?: boolean;
  isPreferredOffer: boolean;
  selectedPreferredOffer: boolean;
}

// El teléfono localizado puede llegar con prefijo o separadores (+34 600 12 34 56),
// pero el input admite 9 dígitos, así que se normaliza antes de pintarlo
const normalizePhone = (phone: string) => `${phone ?? ''}`
  .replace(/[\s.-]/g, '')
  .replace(/^(\+34|0034|34)/, '');

export default function LocalizedBugetOrderOrder({
  // Estado para el id de la oferta seleccionada
  nextStep,
  handleLoading,
  isDocument,
  isPreferredOffer,
  selectedPreferredOffer,
}: LocalizedBugetOrderProps) {
  const [emailValue, setEmailValue] = useState<string>('');
  const [phoneValue, setPhoneValue] = useState<string>('');
  const [emailError, setEmailError] = useState<string>('');
  const [phoneError, setPhoneError] = useState<string>('');
  const [isEmailTouched, setIsEmailTouched] = useState<boolean>(false);
  const [isPhoneTouched, setIsPhoneTouched] = useState<boolean>(false);
  const [price, setPrice] = useState<string | null>('');
  const [cartId, setCartId] = useState<string>('');
  const [priceError, setPriceError] = useState<string>('');
  const [cartIdError, setCartIdError] = useState<string>('');
  const orderData = useSelector((state: RootState) => state.orderData?.data);
  const invoiceData = useSelector((state: RootState) => state.invoiceManaged?.data);
  const offers = useSelector((state: RootState) => state.orderData.simulationsManaged);
  const checkValueOfferAndNoPreferredOffer = useSelector((state: RootState) => state.commercialOffers.data.preferredOfferandNoPreferredOffer);
  const [products, setProducts] = useState<Product[]>([]);
  const [isEditingPrice, setIsEditingPrice] = useState(false);
  const [isEditingCartId, setIsEditingCartId] = useState(false);
  const [title, setTitle] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  // multiples offers
  const isSelectedPreferred = useSelector((state: RootState) => state.orderData.isPreferredOffer);
  const showSelect = useSelector((state: RootState) => state.commercialOffers.data.showSelect);
  const [is_preferred_boolean, setIsPreferredBoolean] = useState<boolean>(isSelectedPreferred);
  const dispatch = useDispatch();
  const simulationsget = useRef<CommercialOfferResult>({ commercial_offers: [] });
  // Guardar todas las simulaciones realizadas
  const allSimulations = useRef<any[]>([]);
  // Ref para el timeout de debounce
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
  // nfc
  const [superExpressFlow] = useState(storageService.hasSuperExpress);
  const [versionControl, setVersionControl] = useState<VersionControl>();
  const [appVersion, setAppVersion] = useState('');
  const [isNFCSupported, setIsNFCSupported] = useState(false);
  const [disabledSelectPreferred, setDisabledSelectPreferred] = useState(false);
  const checkIsVersionControl = useSelector(
    (state: RootState) => state.configSlice.versionControl,
  );
  if (!isPlatform('mobileweb') && !isPlatform('desktop')) {
    App?.getInfo().then((version) => {
      setAppVersion(version.version);
    });
  }
  const checkDefaultSuperExpress = async () => {
    let defaultSuperExpress = false;
    if (!checkIsVersionControl) {
      await requestVersionControl().then((res) => {
        const versionRes = res as VersionControl;
        setVersionControl(versionRes);
        defaultSuperExpress = versionRes.super_express_default;
        dispatch(saveVersionControl(versionRes));
      });
    } else {
      setVersionControl(checkIsVersionControl);
      defaultSuperExpress = checkIsVersionControl.super_express_default;
    }
    Analytics.setAnalyticsUserProperties({
      [ClientTaxonomyProps.SUPER_EXPRESS_ENABLED]:
        (superExpressFlow !== null
          ? superExpressFlow
          : defaultSuperExpress
        ).toString(),
    });
  };
  useEffect(() => {
    if (DniReader && Capacitor.isNativePlatform() && isPlatform('android')) {
      DniReader.isSupported().then((result) => {
        setIsNFCSupported(result.isSupported);
      });
      checkDefaultSuperExpress();
    } else if (isPlatform('ios')) {
      idniecap.isNFCEnable().then((result) => {
        if (result.disponible) {
          setIsNFCSupported(true);
        }
      });
      checkDefaultSuperExpress();
    }
  }, []);
  const { presentModal: presentModalOrderDetail } = useModal(ModalOrderDetail);
  const { presentModal: presentModalSimulation } = useModal(ModalSimulation);

  useEffect(
    () => {
      if (isDocument) {
        InsightService.logTrace('1. MANAGED - carga presupuesto localizado');
        setTitle(TitleOrderLocalized.INVOICE_LOCALIZED);
        setDescription(DescriptionOrderLocalized.DESCRIPTION_FILE);
      } else {
        InsightService.logTrace('1. MANAGED - carga pedido localizado');
        setTitle(TitleOrderLocalized.ORDER_LOCALIZED);
        setDescription(DescriptionOrderLocalized.DESCRIPTION_ORDER);
      }
    },
    [],
  );
  useEffect(() => {
    if (!isDocument && orderData) {
      InsightService.logTrace('2. MANAGED - validación y datos de pedido localizado', [
        { isDocument },
        { orderData },
      ]);
      setPrice(orderData.data.total_price.toString() || '');
      setCartId(orderData.data.cart.id.toString() || '');
      setProducts(orderData.data.cart.products);

      if (orderData.data.total_price) {
        getInitialSimulation(orderData.data.total_price.toString());
      }

      // Establece y valida el email
      const { email } = orderData.data.applicant;
      changeEmailInput(email, Boolean(email)); // Valida el email

      // Establece y valida el teléfono
      const phone = orderData.data.applicant.billing_address.phone || orderData.data.applicant.shipping_address?.phone || '';
      changePhoneInput(normalizePhone(phone), Boolean(phone)); // Valida el teléfono
    }
  }, [isDocument, orderData]);

  useEffect(() => {
    if (isDocument && invoiceData) {
      InsightService.logTrace('2. MANAGED - validación y datos de presupuesto localizado', [
        { isDocument },
        { invoiceData },
      ]);
      setPrice(invoiceData.data.third_party_response.total_price.toString());
      setCartId(invoiceData.data.third_party_response.cart.id);
      setProducts(invoiceData.data.third_party_response.cart.products);

      changePriceInput(invoiceData.data.third_party_response.cart.cart_price!.toString());
      getInitialSimulation(invoiceData.data.third_party_response.cart.cart_price!.toString());

      // Establece y valida el email
      const email = invoiceData.data.third_party_response.applicant.email || '';
      changeEmailInput(email, Boolean(email)); // Valida el email

      // Establece y valida el teléfono
      const phone = invoiceData.data.third_party_response.applicant.billing_address?.phone
        || invoiceData.data.third_party_response.applicant.shipping_address?.phone
        || '';

      changePhoneInput(normalizePhone(phone), Boolean(phone)); // Valida el teléfono
    }
  }, [isDocument, invoiceData]);

  const changeEmailInput = (value: string, setTouched = false) => {
    // El valor siempre se sincroniza con el estado, aunque el campo no esté "touched",
    // para que no se pierda lo que se teclea hasta el blur
    setEmailValue(value);

    if (!isEmailTouched && setTouched) {
      setIsEmailTouched(true);
    }

    // La validación se ejecuta siempre para que el botón Continuar refleje el estado real;
    // el mensaje de error solo se pinta cuando el campo está "touched"
    if (!value) {
      setEmailError('Introduce un email válido');

      return;
    }

    // Validación del email
    if (!VALIDATOR_PATTERN.EMAIL.test(value)) {
      setEmailError('Introduce un email válido');
    } else {
      setEmailError('');
    }
  };

  const changePhoneInput = (value: string, setTouched = false) => {
    // El valor siempre se sincroniza con el estado, aunque el campo no esté "touched",
    // para que no se pierda lo que se teclea hasta el blur
    setPhoneValue(value);

    if (!isPhoneTouched && setTouched) {
      setIsPhoneTouched(true);
    }

    // La validación se ejecuta siempre para que el botón Continuar refleje el estado real;
    // el mensaje de error solo se pinta cuando el campo está "touched"
    if (!value) {
      setPhoneError('Indica tu móvil');

      return;
    }
    let validPhone = false;
    // Verifica si el número tiene caracteres repetidos
    for (let i = 1; i < value.length; i++) {
      if (value[i] !== value[0]) {
        validPhone = true;
        break;
      }
    }
    // Validación del número de teléfono
    if (!VALIDATOR_PATTERN.PHONE.test(value) || !validPhone) {
      setPhoneError('Móvil inválido');
    } else {
      setPhoneError('');
    }
  };

  const blockCharacters = (event: any) => ['e', 'E', '+', '-'].includes(event.key) && event.preventDefault();

  const changePriceInput = async (value: string) => {
    clearTimeout(debounceTimeout.current!);
    let errorPrice: boolean = false;
    if (value) {
      const numericValue = Number(value);

      // Si es 0 o un número negativo, muestra un error
      if (numericValue <= 0) {
        setPriceError('Introduce un importe válido');
        errorPrice = true;
        setPrice(value);
        return;
      }

      // Verifica si el precio está dentro del rango de la oferta comercial
      const isValidOffer = await checkCommercialOfferAvailabilityECO(numericValue);
      if (!isValidOffer) {
        setPriceError('Introduce un importe válido');
        errorPrice = true;
        setIsEditingPrice(true);
        setPrice(value);
        return;
      }

      // Si todo está bien, actualiza el precio y limpia el error
      setPrice(value);
      setPriceError('');
      errorPrice = false;
      if (!(isEditingPrice === true && value === price && (!showSelect && !checkValueOfferAndNoPreferredOffer))) {
        debounceTimeout.current = setTimeout(() => {
          if ((showSelect || checkValueOfferAndNoPreferredOffer) && !errorPrice) {
            priceInputRef.current?.blur();
            setIsEditingPrice(false);
            getInitialSimulation(formatPrice(Number(value)));
          }
        }, 1500);
      }
    } else {
      // Si el valor está vacío, muestra un error
      setPriceError('Introduce un importe válido');
      setPrice(value);
    }
  };
  const filterIsPreferredOffer = (simulations: CommercialOfferResult, idCommercialdValue: boolean): CommercialOfferResult => {
    // 1. Filtra por commercial_offer_id
    if (checkValueOfferAndNoPreferredOffer) {
      /*  const selectedOffer = simulations.commercial_offers.find(
         (simulation: any) => simulation.is_preferred === idCommercialdValue
       ); */
      // 2. Filtra todas las commercial_offers que tengan el mismo is_preferred
      const commercial_offers_filtered = simulations.commercial_offers.filter(
        (simulation: any) => simulation.is_preferred === idCommercialdValue,
      );
      return {
        commercial_offers: commercial_offers_filtered,
      };
      // eslint-disable-next-line no-else-return
    } else {
      return { commercial_offers: simulations.commercial_offers };
    }
  };

  const calculatedFinancialOffers = (simulationFilter: CommercialOfferResult) => {
    let financialOffers: any[] = [];
    if (simulationFilter.commercial_offers.length > 0) {
      simulationFilter.commercial_offers.map((offers: CommercialOfferType) => {
        financialOffers.push(...offers.financial_offers);
        financialOffers.sort((a, b) => a.term - b.term);
      });
    } else {
      financialOffers = simulationFilter.commercial_offers[0].financial_offers;
    }
    dispatch(saveBudgetSimulations(financialOffers));
  };

  // Simulación inicial durante la primera pantalla de carga.
  // Con presupuestos, si se edita el importe de compra
  // las siguientes simulaciones se hacen desde el modal
  const getInitialSimulation = async (amount: string) => {
    setDisabledSelectPreferred(true);
    amount = amount.replace(',', '.');
    try {
      if (amount && await checkCommercialOfferAvailabilityECO(parseFloat(amount))) {
        const sim = {
          amount: parseFloat(amount),
          flows: ['ECO'],
          // is_preferred: true,
        };
        // Busca si ya existe una simulación igual y recupera sus datos
        const found = allSimulations.current.find((s) => JSON.stringify(s.sim) === JSON.stringify(sim));
        if (found) {
          simulationsget.current = found.data;
          setDisabledSelectPreferred(false);
        } else {
          // Si no existe, realiza la simulación y guarda el resultado
          // eslint-disable-next-line prefer-destructuring
          const data = (await getSimulations(sim)).data;
          allSimulations.current.push({ sim, data });
          simulationsget.current = data;
        }
        const yespreferredOffer = simulationsget.current.commercial_offers.some(
          (commercialOffer: CommercialOfferType) => commercialOffer.is_preferred,
        );
        const noPreferredOffer = simulationsget.current.commercial_offers.some(
          (commercialOffer: CommercialOfferType) => !commercialOffer.is_preferred,
        );

        dispatch(checkpreferredOfferAndNoPreferredOffer(yespreferredOffer && noPreferredOffer));

        // Selecciona por defecto el valor de is_preferred_boolean según la oferta seleccionada
        if (simulationsget.current.commercial_offers.length > 0) {
          // Si hay una oferta con el mismo is_preferred_boolean actual, mantenlo; si no, selecciona la primera disponible
          const match = simulationsget.current.commercial_offers.find((offer: CommercialOfferType) => offer.is_preferred === is_preferred_boolean);
          if (match) {
            setIsPreferredBoolean(match.is_preferred);
            dispatch(saveIsPreferredOffer(match.is_preferred));
          } else {
            setIsPreferredBoolean(simulationsget.current.commercial_offers[0].is_preferred);
            dispatch(saveIsPreferredOffer(simulationsget.current.commercial_offers[0].is_preferred));
          }
        } else {
          setIsPreferredBoolean(false);
          dispatch(saveIsPreferredOffer(false));
        }
        const simulationFilter = filterIsPreferredOffer(simulationsget.current, is_preferred_boolean);
        calculatedFinancialOffers(simulationFilter);
      }
    } catch (error) {
      setDisabledSelectPreferred(false);
    }
    setDisabledSelectPreferred(false);
  };

  const changeCartInput = (value: string) => {
    if (value) {
      // Si es 0 o un número negativo
      if (Number(value) <= 0) {
        setCartIdError('Introduce una referencia válida');
        setCartId(value);
        return;
      }
      // Si el valor es válido
      setCartIdError('');
      setCartId(value);
    } else {
      // Si el valor está vacío
      setCartIdError('Introduce una referencia válida');
      setCartId(value);
    }
  };
  const updateOrderData = (orderUpdate: OrderDetailsResponseContent) => {
    InsightService.logTrace('3. MANAGED - formateo datos pedido localizado');
    if (!orderUpdate) return;
    const updatedOrderData: OrderDetailsResponseContent = {
      applicant: {
        ...orderUpdate.applicant,
        email: emailValue,
        ...(orderUpdate.applicant.billing_address && {
          billing_address: {
            phone: phoneValue,
            country: orderUpdate.applicant.billing_address?.country || '',
            city: orderUpdate.applicant.billing_address?.city || '',
            province: orderUpdate.applicant.billing_address?.province || '',
            postal_code: orderUpdate.applicant.billing_address?.postal_code || '',
            address: orderUpdate.applicant.billing_address?.address || '',
          },
        }),
        ...(orderUpdate.applicant.shipping_address && {
          shipping_address: {
            phone: orderUpdate.applicant.shipping_address?.phone || '',
            country: orderUpdate.applicant.shipping_address?.country || '',
            city: orderUpdate.applicant.shipping_address?.city || '',
            province: orderUpdate.applicant.shipping_address?.province || '',
            postal_code: orderUpdate.applicant.shipping_address?.postal_code || '',
            address: orderUpdate.applicant.shipping_address?.address || '',
          },
        }),
      },
      status: orderUpdate.status,
      order_id: orderUpdate.order_id,
      platform: orderUpdate.platform,
      order_date: orderUpdate.order_date,
      term: orderUpdate.term,
      total_products: orderUpdate.total_products,
      total_price: Number(price),
      url_callback: orderUpdate.url_callback,
      cart: {
        ...orderUpdate.cart,
        id: cartId,
        cart_price: Number(price),
        shipping_price: orderUpdate.cart.shipping_price,
        products: orderUpdate.cart.products.map((product) => ({
          ...product,
        })),
      },
      total_refunded: orderUpdate.total_refunded,
      url_access: orderUpdate.url_access,
      has_consent: orderUpdate.has_consent,
      process_type: orderUpdate.process_type,
      view_mode: orderUpdate.view_mode,
      input_mode: orderUpdate.input_mode,
      is_preferred: is_preferred_boolean,
    };
    // Actualiza el estado de orderManaged con los nuevos datos
    dispatch(saveOrderManagedData({ data: updatedOrderData }));
    handleLoading(false);
    if (
      appVersion
      && (superExpressFlow !== null
        ? superExpressFlow
        : versionControl?.super_express_default)
      && isNFCSupported
    ) {
      InsightService.logTrace('4. MANAGED - Navegar a DNIe desde pedido localizado');
      // Si es NFC y está soportado, redirige a la pantalla de DNIe
      nextStep(ManagedModalRoutes.DniSCAN_MANAGED, false, is_preferred_boolean);
    } else {
      InsightService.logTrace('4. MANAGED - Navegar a datos personales desde pedido localizado');
      // Si no es NFC o no está soportado, redirige a la pantalla de datos personales
      nextStep(ManagedModalRoutes.ORDER_PERSONAL_DATA, false, is_preferred_boolean);
    }
  };

  // DATOS SOLO DE TEST PARA ENTORNO DE DEV -------------------------
  const initTestData = async () => {
    try {
      if (isDocument) {
        InsightService.logTrace('3. MANAGED - formateo datos presupuesto localizado');
        handleLoading(true);
        if (!invoiceData) return;
        const updatedInvoiceData: InvoiceData = {
          third_party_response: {
            ...invoiceData.data.third_party_response,
            is_preferred: is_preferred_boolean,
            cart: {
              ...invoiceData.data.third_party_response.cart,
              id: cartId,
              cart_price: Number(price),
              products: invoiceData.data.third_party_response.cart.products.map((product) => ({
                ...product,
              })),
            },
            total_price: Number(price),
            applicant: {
              ...invoiceData.data.third_party_response.applicant,
              email: emailValue,
              group: 'Store',
              billing_address: {
                ...invoiceData.data.third_party_response.applicant.billing_address,
                phone: phoneValue,
                country: invoiceData.data.third_party_response.applicant.billing_address?.country || '',
                city: invoiceData.data.third_party_response.applicant.billing_address?.city || '',
                province: invoiceData.data.third_party_response.applicant.billing_address?.province || '',
                postal_code: invoiceData.data.third_party_response.applicant.billing_address?.postal_code || '',
                address: invoiceData.data.third_party_response.applicant.billing_address?.address || '',
              },
              shipping_address: {
                ...invoiceData.data.third_party_response.applicant.shipping_address,
                phone: invoiceData.data.third_party_response.applicant.shipping_address?.phone || '',
                country: invoiceData.data.third_party_response.applicant.shipping_address?.country || '',
                city: invoiceData.data.third_party_response.applicant.shipping_address?.city || '',
                province: invoiceData.data.third_party_response.applicant.shipping_address?.province || '',
                postal_code: invoiceData.data.third_party_response.applicant.shipping_address?.postal_code || '',
                address: invoiceData.data.third_party_response.applicant.shipping_address?.address || '',
              },
            },
          },
          status: invoiceData.data.status,
          global_attempts_remaining: invoiceData.data.global_attempts_remaining,
          analyze_attempts_remaining: invoiceData.data.analyze_attempts_remaining,
        };
        // Actualiza el estado de invoiceManaged con los nuevos datos
        dispatch(saveInvoiceManagedData({ data: updatedInvoiceData }));
        handleLoading(false);

        InsightService.logTrace('4. MANAGED - Navegar a DNIe desde presupuesto localizado');
        nextStep(ManagedModalRoutes.DniSCAN_MANAGED, true, is_preferred_boolean);
      } else if (orderData) {
        handleLoading(true);
        updateOrderData(orderData.data);
      } else {
        nextStep(ManagedModalRoutes.UNKNOWN_ERROR);
      }
    } catch (error) {
      handleLoading(false);
      nextStep(ManagedModalRoutes.UNKNOWN_ERROR);
    }
  };
  //----------------------------------------------------------------
  const handleContinue = async () => {
    // actualizamos el estado de invoiceManaged u orderManaged
    try {
      if (isDocument) {
        // PRESUPUESTO - INVOICE
        InsightService.logTrace('3. MANAGED - formateo datos presupuesto localizado');
        handleLoading(true);
        if (!invoiceData) return;
        const updatedInvoiceData: InvoiceData = {
          third_party_response: {
            ...invoiceData.data.third_party_response,
            is_preferred: is_preferred_boolean,
            cart: {
              ...invoiceData.data.third_party_response.cart,
              id: cartId,
              cart_price: Number(price),
              products: invoiceData.data.third_party_response.cart.products.map((product) => ({
                ...product,
              })),
            },
            total_price: Number(price),
            applicant: {
              ...invoiceData.data.third_party_response.applicant,
              email: emailValue,
              group: 'Store',
              billing_address: {
                ...invoiceData.data.third_party_response.applicant.billing_address,
                phone: phoneValue,
                country: invoiceData.data.third_party_response.applicant.billing_address?.country || '',
                city: invoiceData.data.third_party_response.applicant.billing_address?.city || '',
                province: invoiceData.data.third_party_response.applicant.billing_address?.province || '',
                postal_code: invoiceData.data.third_party_response.applicant.billing_address?.postal_code || '',
                address: invoiceData.data.third_party_response.applicant.billing_address?.address || '',
              },
              shipping_address: {
                ...invoiceData.data.third_party_response.applicant.shipping_address,
                phone: invoiceData.data.third_party_response.applicant.shipping_address?.phone || '',
                country: invoiceData.data.third_party_response.applicant.shipping_address?.country || '',
                city: invoiceData.data.third_party_response.applicant.shipping_address?.city || '',
                province: invoiceData.data.third_party_response.applicant.shipping_address?.province || '',
                postal_code: invoiceData.data.third_party_response.applicant.shipping_address?.postal_code || '',
                address: invoiceData.data.third_party_response.applicant.shipping_address?.address || '',
              },
            },
          },
          status: invoiceData.data.status,
          global_attempts_remaining: invoiceData.data.global_attempts_remaining,
          analyze_attempts_remaining: invoiceData.data.analyze_attempts_remaining,
        };
        // Actualiza el estado de invoiceManaged con los nuevos datos
        dispatch(saveInvoiceManagedData({ data: updatedInvoiceData }));
        handleLoading(false);

        if (
          appVersion
          && (superExpressFlow !== null
            ? superExpressFlow
            : versionControl?.super_express_default)
          && isNFCSupported
        ) {
          // Si es NFC y está soportado, redirige a la pantalla de DNIe
          InsightService.logTrace('4. MANAGED - Navegar a DNIe desde presupuesto localizado');
          nextStep(ManagedModalRoutes.DniSCAN_MANAGED, true, is_preferred_boolean);
        } else {
          // Si no es NFC o no está soportado, redirige a la pantalla de datos personales
          InsightService.logTrace('4. MANAGED - Navegar a datos personales desde presupuesto localizado');
          nextStep(ManagedModalRoutes.ORDER_PERSONAL_DATA, true, is_preferred_boolean);
        }
      } else if (orderData) {
        // ORDER
        handleLoading(true);
        updateOrderData(orderData.data);
      } else {
        nextStep(ManagedModalRoutes.UNKNOWN_ERROR);
      }
    } catch (error) {
      handleLoading(false);
      nextStep(ManagedModalRoutes.UNKNOWN_ERROR);
    }
  };

  const cartIdInputRef = useRef<HTMLIonInputElement>(null);

  useEffect(() => {
    // el campo obtenga el foco solo
    if (isEditingCartId && cartIdInputRef.current) {
      setTimeout(() => {
        cartIdInputRef.current?.setFocus(); // Enfoca
      }, 0);
    }
  }, [isEditingCartId]);

  const priceInputRef = useRef<HTMLIonInputElement>(null);

  useEffect(() => {
    // el campo obtenga el foco solo
    if (isEditingPrice && priceInputRef.current) {
      setTimeout(() => {
        priceInputRef.current?.setFocus(); // Enfoca
      }, 0);
    }
  }, [isEditingPrice]);

  return (
    <div className={styles.modalLocalizedBudgetOrderContainer}>
      <div className={styles.orderLocalizedBudgetOrderForm}>
        <div className={styles.orderManagedTitleDescriptionWrapper}>
          <div className={styles.orderManagedTitleWrapper}>
            <IonLabel className={styles.modalOrderManagedTitle}>{title}</IonLabel>
          </div>
          <div className={styles.orderManagedTitleWrapper}>
            <IonLabel className={styles.modalOrderManagedDescription}>{description}</IonLabel>
          </div>
        </div>
        <IonGrid className={classNames(styles.modalFormWrapperEdit, styles.formInvoiceData)}>
          <IonRow className={styles.modalFormRow}>
            <IonCol>
              <div
                className={classNames(
                  styles.entrySummaryEdit,
                  styles.inputFirstForm,
                  {
                    'ion-invalid': priceError,
                    'ion-valid': !priceError,
                  },
                )}
              >
                <div className={styles.summaryOrderData}>
                  <IonLabel className={styles.labelPrice}>Importe de la compra</IonLabel>
                  <div className={styles.priceContainer}>
                    {isEditingPrice ? (
                      <IonInput
                        type="number"
                        ref={priceInputRef}
                        autoFocus
                        value={price}
                        onIonInput={(e: any) => {
                          dispatch(clearBudgetSimulations());
                          dispatch(clearSimulationTerm());
                          changePriceInput(String(e.target.value));
                        }}
                        onBlur={(e: any) => {
                          setIsEditingPrice(false);
                          if ((checkValueOfferAndNoPreferredOffer) && price && !priceError) {
                            getInitialSimulation(formatPrice(e.target.value));
                          }
                        }}
                        onIonBlur={(e: any) => { changePriceInput(String(e.target.value)); }}
                        className={styles.priceInput}
                        errorText={priceError}
                        onKeyDown={blockCharacters}
                        max={20000}
                      />
                    ) : (

                      <IonLabel className={styles.priceLabel}>
                        {formatPrice(Number(price))}
                      </IonLabel>

                    )}
                  </div>
                  {isDocument && (
                    <IonIcon
                      src={EditIcon}
                      className={styles.entryEditBtn}
                      onClick={() => setIsEditingPrice(true)} // Activa el modo edición
                    />
                  )}
                </div>
              </div>
            </IonCol>
          </IonRow>
          {priceError && <span className={styles.validationError}>{priceError}</span>}
          <IonRow className={styles.modalFormRow}>
            <IonCol>
              <div
                className={classNames(styles.entrySummaryEdit, {
                  'ion-invalid': cartIdError,
                  'ion-valid': !cartIdError,
                })}
              >
                <div className={styles.summaryOrderData}>
                  <IonLabel>Referencia</IonLabel>
                  <div className={styles.referenceContainer}>
                    {isEditingCartId ? (
                      <IonInput
                        ref={cartIdInputRef}
                        type="text"
                        value={cartId}
                        maxlength={100}
                        onIonInput={(e: any) => changeCartInput(e.target.value)}
                        autoFocus
                        onIonBlur={(e: any) => { changeCartInput(e.target.value); }}
                        onBlur={() => setIsEditingCartId(false)}
                        className={styles.referenceInput}
                        errorText={cartIdError}
                      />
                    ) : (
                      <IonLabel className={styles.referenceLabel}>{cartId}</IonLabel>
                    )}
                  </div>
                  {isDocument && (
                    cartId ? (
                      <IonIcon
                        src={EditIcon}
                        className={styles.entryEditBtn}
                        onClick={() => setIsEditingCartId(true)}
                      />
                    ) : (
                      <IonImg
                        src={IconEditManaged}
                        alt="Icon"
                        className={styles.entryEditBtn}
                        onClick={() => setIsEditingCartId(true)}
                      />
                    )
                  )}
                </div>
              </div>
            </IonCol>
          </IonRow>
          {cartIdError && <span className={styles.validationError}>{cartIdError}</span>}
          <IonRow className={styles.modalFormRow}>
            <IonCol>
              <div className={styles.entrySummary}>
                <div className={styles.summaryOrderData}>
                  <IonLabel>Detalle de productos</IonLabel>
                  <IonLabel className={styles.priceContainer}>
                    { }
                  </IonLabel>
                  <IonIcon
                    src={InfoIcon}
                    className={styles.entryEditBtn}
                    onClick={() => {
                      presentModalOrderDetail({
                        modalInModal: true,
                        onDismiss: () => { },
                        products,
                      });
                    }}
                  />

                </div>
              </div>
            </IonCol>
          </IonRow>
        </IonGrid>
        {checkValueOfferAndNoPreferredOffer && simulationsget.current.commercial_offers.length > 0 && (
          <div className={styles.merchantSection}>
            <IonLabel className={styles.modalTitle}>Tarifa</IonLabel>
            <IonRow className={styles.modalFormRow}>
              <IonCol>
                <IonList lines="full">
                  <IonSelect
                    labelPlacement="floating"
                    className={classNames(styles.preferredSelectBtn)}
                    value={is_preferred_boolean}
                    interface="popover"
                    disabled={simulationsget.current.commercial_offers.length === 0 || disabledSelectPreferred}
                    onIonChange={(e: any) => {
                      setIsPreferredBoolean(e.detail.value);
                      dispatch(saveIsPreferredOffer(e.detail.value));

                      const simulationFilter = filterIsPreferredOffer(simulationsget.current, e.detail.value);
                      calculatedFinancialOffers(simulationFilter);
                    }}
                    toggleIcon={chevronDownOutline}
                  >
                    <IonSelectOption key="non_preferred" value={false} className={styles.selectPreferredProductOption}>
                      TARIFA NO CLUB
                    </IonSelectOption>
                    <IonSelectOption key="preferred" value={true} className={styles.selectPreferredProductOption}>
                      TARIFA CLUB
                    </IonSelectOption>
                  </IonSelect>
                </IonList>
              </IonCol>
            </IonRow>
          </div>
        )}
        {!priceError && price
          && (
            <IonButtons className={styles.simulationBtnWrapper}>
              <IonButton
                className={styles.simulationBtn}
                disabled={disabledSelectPreferred || (isEditingPrice && checkValueOfferAndNoPreferredOffer)}
                onClick={async () => {
                  presentModalSimulation({
                    currentPrice: price && parseFloat(price),
                    terms: offers?.length,
                    modalInModal: true,
                    onDismiss: () => { },
                  });
                }}
              >
                Simular financiación
                <IonIcon src={CalculatorIcon} className={styles.simulationCalculatorIcon} />
              </IonButton>
            </IonButtons>
          )}
      </div>
      <div className={styles.orderLocalizedBudgetOrderForm}>
        <IonLabel className={styles.modalTitle}>Datos de contacto cliente</IonLabel>
        <IonGrid className={classNames(styles.modalFormWrapper, styles.personalDataForm)}>
          <IonRow className={styles.modalFormRow}>
            <IonCol>
              <IonInput
                type="email"
                label="Email"
                value={emailValue}
                labelPlacement="floating"
                className={classNames(styles.systemFont, styles.localizedInput, styles.upperInput, {
                  'ion-invalid': isEmailTouched && emailError,
                  'ion-valid': !(isEmailTouched && emailError),
                })}
                errorText={isEmailTouched ? emailError : ''}
                onIonInput={(e: any) => {
                  changeEmailInput(e.target.value, true);
                }}
                onIonBlur={(e: any) => changeEmailInput(e.target.value, true)}
              />
              {isEmailTouched && emailError
                && <span className={styles.validationError}>{emailError}</span>}
            </IonCol>
          </IonRow>
          <IonRow className={styles.modalFormRow}>
            <IonCol>
              <IonInput
                id="phone"
                name="phone"
                type="tel"
                maxlength={9}
                value={phoneValue}
                label="Teléfono móvil"
                labelPlacement="floating"
                className={classNames(
                  styles.systemFont,
                  styles.formAmountInput,
                  styles.lowerInput,
                  styles.localizedInput,
                  {
                    'ion-invalid': isPhoneTouched && phoneError,
                    'ion-valid': !(isPhoneTouched && phoneError),
                  },
                )}
                errorText={isPhoneTouched ? phoneError : ''}
                onIonInput={(e: any) => changePhoneInput(e.target.value, true)}
                onIonBlur={(e: any) => changePhoneInput(e.target.value, true)}
              />
              {isPhoneTouched && phoneError
                && <span className={styles.validationError}>{phoneError}</span>}
            </IonCol>
          </IonRow>
        </IonGrid>
      </div>
      <div className={styles.modalOrderManagedButtonsWrapper}>
        {ENVIRONMENT.ENV === 'development' && <button onClick={initTestData} type="button">Test value ir a lector dni</button>}

        <IonButtons className={styles.nextBtnWrapper}>
          <IonButton className="nextButton" onClick={handleContinue} disabled={!emailValue || !phoneValue || !!emailError || !!phoneError || !!priceError || !cartId || !price}>
            Continuar
          </IonButton>
        </IonButtons>
      </div>
    </div>
  );
}
